import { useAxios } from './useAxios';
import { PowerProfileDataPoint } from 'greener-ui-library';
import { useSnackbar } from 'notistack';
import { parsePowerProfileCsv } from 'pages/Blueprints/utils/parsePowerProfileCsv';
import { useTranslation } from 'react-i18next';

export interface PowerProfileValidationResponse {
  success: boolean;
  data?: PowerProfileDataPoint[];
  errors?: string[];
  warnings?: string[];
  message?: string;
}

export const useValidatePowerProfileFile = () => {
  const { axiosRequest } = useAxios();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const validateFile = async (
    file: File,
    responseFormat: 'csv' | 'json' = 'csv'
  ): Promise<PowerProfileValidationResponse> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const config = {
        method: 'POST' as const,
        path: `/blueprints/power-profile/validate-and-process/?response_format=${responseFormat}`,
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      };

      const response = await axiosRequest(config);

      const csvData = response.data as string;
      const dataPoints = parsePowerProfileCsv(csvData);

      return {
        success: true,
        data: dataPoints,
        message: 'File validated successfully',
      };
    } catch (error: unknown) {
      console.error('Power profile file validation failed:', error);

      // Handle errors from backend
      let errorMessage = 'File validation failed';
      let errors: string[] = [];

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown } };
        if (axiosError.response?.data) {
          const responseData = axiosError.response.data;
          // If backend returns structured errors
          if (
            responseData &&
            typeof responseData === 'object' &&
            'errors' in responseData &&
            Array.isArray(responseData.errors)
          ) {
            errors = responseData.errors as string[];
          } else if (responseData && typeof responseData === 'object' && 'detail' in responseData) {
            // Backend can return detail as string or array of strings
            if (typeof responseData.detail === 'string') {
              errorMessage = responseData.detail;
            } else if (Array.isArray(responseData.detail)) {
              // Join multiple error messages with newlines
              errorMessage = responseData.detail.join('\n');
            }
          } else if (
            responseData &&
            typeof responseData === 'object' &&
            'message' in responseData &&
            typeof responseData.message === 'string'
          ) {
            errorMessage = responseData.message;
          } else if (typeof responseData === 'string') {
            errorMessage = responseData;
          }
        }
      } else if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
        errorMessage = error.message;
      }

      enqueueSnackbar(
        t('blueprints:wizard.fields.powerProfile.validationError', {
          error: errorMessage,
        }),
        { variant: 'error' }
      );

      return {
        success: false,
        errors: errors.length > 0 ? errors : [errorMessage],
        message: errorMessage,
      };
    }
  };

  return { validateFile };
};
